import { Component, Input } from '@angular/core';
import { Columns } from '@shared/interfaces/table/column-config';
import { ValueGroupComponent } from '@shared/components/input/value-group/value-group.component';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { ValueDataRendererComponent } from '@shared/components/value-data-renderer/value-data-renderer.component';
import { TranslatePipe } from '@ngx-translate/core';

/**
 * A generic, configurable component for rendering a list of labeled values based on a column configuration.
 *
 * Each value is resolved dynamically from the `data` input using a key path (e.g., 'user.address.city').
 *
 * Designed to work with table-style data structures and metadata-driven UIs.
 */
@Component({
  selector: 'app-value-details',
  imports: [
    ValueGroupComponent,
    LabelDirective,
    ValueDataRendererComponent,
    TranslatePipe,
  ],
  templateUrl: './value-details.component.html',
  styleUrl: './value-details.component.css'
})
export class ValueDetailsComponent<T> {
  /**
   * A collection of column definitions that determine which fields to render and how.
   */
  @Input() public collection!: Columns<T>[];

  /**
   * The data object containing values to be rendered based on the column configuration.
   */
  @Input() public data!: any;

  /**
   * Resolves a deeply nested key path into a value from the `data` object.
   *
   * Supports both dot-separated strings (e.g., 'user.name') and arrays of key paths.
   *
   * @param key - A string or array representing the key path to resolve.
   * @returns The resolved value from the `data` object, or `undefined` if not found.
   */
  public resolveKey(key: any): any {
    if (Array.isArray(key)) {
      return key.map(k => this.resolveKey(k));
    }

    return key.split('.').reduce((acc: any, part: any) => acc?.[part], this.data);
  }
}
